<template>
  <div class="mb-16 bg-card rounded-lg shadow-sm p-8 border border-border">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-semibold">Latest Arrivals</h2>
      <NuxtLink 
        :to="{ path: '/vehicles', query: currentFilterQuery }" 
        class="inline-flex items-center text-primary hover:underline"
      >
        View All 
        <ChevronRightIcon class="ml-1 h-4 w-4" />
      </NuxtLink>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div 
        v-for="vehicle in latestVehiclesFromSources" 
        :key="vehicle.id"
        class="group relative bg-white dark:bg-black rounded-xl overflow-hidden vehicle-card-animation flex flex-col h-full border border-border hover:border-primary transition-colors"
      >
        <!-- Vehicle Image -->
        <div class="relative aspect-16/9">
          <NuxtLink :to="getVehicleUrl(vehicle)">
            <img 
              :src="getVehicleImage(vehicle)"
              :alt="getVehicleTitle(vehicle)" 
              class="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            >
          </NuxtLink>
          
          <!-- Price Badge -->
          <div class="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/80 to-transparent p-4">
            <div class="flex items-end justify-between">
              <div>
                <div class="text-white font-bold text-xl">
                  ${{ normalizePrice(vehicle.price)?.toLocaleString() }}
                </div>
                <div v-if="vehicle.perweek" class="text-white/80 text-sm">
                  ${{ Math.round(normalizePrice(vehicle.perweek)) }}/week
                </div>
              </div>
              <FavoriteButton :vehicle-id="vehicle.id" class="text-white" />
            </div>
          </div>

          <div class="absolute top-2 left-2 flex flex-wrap gap-1">
            <span class="px-2 py-1 bg-black/70 text-white text-xs rounded-full">
              {{ vehicle.condition?.displayValue?.[0] || 'Used' }}
            </span>
            <span v-if="vehicle.isNew" class="px-2 py-1 bg-red-600 text-white text-xs rounded-full">New Arrival</span>
            <span v-if="vehicle.isLatestFromSource" class="px-2 py-1 bg-green-600 text-white text-xs rounded-full">Latest</span>
          </div>
        </div>
        
        <!-- Card Content -->
        <div class="flex-1 p-4 flex flex-col">
          <!-- Header -->
          <header class="mb-3">
            <div class="flex items-start justify-between gap-4">
              <h2 class="text-xl font-bold leading-tight text-slate-900 dark:text-white">
                {{ getVehicleTitle(vehicle) }}
              </h2>
            </div>
          </header>
          
          <!-- Vehicle details -->
          <div class="space-y-4 flex-1">
            <div class="grid grid-cols-2 gap-y-3">
              <!-- Body Type -->
              <div class="flex items-center gap-2 text-sm text-gray-600">
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 9h18v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9zm0 0l2-4h14l2 4" />
                </svg>
                {{ vehicle.body?.displayValue?.[0] || 'Not specified' }}
              </div>

              <!-- Transmission -->
              <div class="flex items-center gap-2 text-sm text-gray-600">
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 6l6 6-6 6" />
                </svg>
                {{ vehicle.transmission?.displayValue?.[0] || 'Not specified' }}
              </div>

              <!-- Kilometers -->
              <div class="flex items-center gap-2 text-sm text-gray-600">
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="8" />
                  <path d="M12 8v4l2 2" />
                </svg>
                {{ vehicle.kms ? `${vehicle.kms.toLocaleString()} km` : 'Not specified' }}
              </div>

              <!-- Fuel Type -->
              <div class="flex items-center gap-2 text-sm text-gray-600">
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M19 8C20.6569 8 22 9.34315 22 11V17C22 18.6569 20.6569 20 19 20H16.5C15.9477 20 15.5 19.5523 15.5 19V9C15.5 8.44772 15.9477 8 16.5 8H19Z" />
                  <path d="M6 3C6 2.44772 6.44772 2 7 2H12C12.5523 2 13 2.44772 13 3V22H6V3Z" />
                  <path d="M6 14H13" />
                  <path d="M4 14H5" />
                  <path d="M17 14C17 14 19 11.9948 19 11" />
                </svg>
                {{ vehicle.fuel?.displayValue?.[0] || 'Not specified' }}
              </div>
            </div>
          </div>
          
          <!-- Actions -->
          <div class="flex flex-wrap items-center gap-2 mt-auto pt-4">
            <button
              @click="searchSimilar(vehicle)"
              class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 transition-colors"
              title="Search similar vehicles"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
              </svg>
              Similar
            </button>
            <button
              @click="handleQuickView(vehicle.id)"
              class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Quick View
            </button>
            <NuxtLink 
              :to="getVehicleUrl(vehicle)"
              class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              View Details
            </NuxtLink>
            <!-- Add to Cart Button -->
            <ClientOnly>
              <button
                @click="addToCart(vehicle.id)"
                class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-secondary px-3 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/90 transition-colors"
                :class="{ '!bg-blue-100 dark:!bg-blue-900/30 !text-blue-600 dark:!text-blue-400 hover:!bg-blue-200 dark:hover:!bg-blue-800/50': cartStore.isInCart(vehicle.id) }" 
                :title="cartStore.isInCart(vehicle.id) ? 'In Cart' : 'Add to cart'"
              >
                 <ShoppingCartIcon 
                    class="h-5 w-5 mr-1.5" 
                    :class="cartStore.isInCart(vehicle.id) ? 'text-blue-600 dark:text-blue-400' : ''" 
                 />
                {{ cartStore.isInCart(vehicle.id) ? 'In Cart' : 'Add to Cart' }}
              </button>
              <template #fallback>
                <button
                  class="flex-1 inline-flex justify-center items-center whitespace-nowrap rounded-lg bg-secondary px-3 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/90 transition-colors"
                >
                  <ShoppingCartIcon class="h-5 w-5 mr-1.5" />
                  Add to Cart
                </button>
              </template>
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>

    <!-- Add VehicleQuickView component -->
    <VehicleQuickView
      :vehicle-id="selectedVehicleId"
      :is-open="isQuickViewOpen"
      :filtered-vehicle-ids="filteredVehicleIds"
      @update:is-open="isQuickViewOpen = $event"
      @close="handleQuickViewClose"
      @navigate="handleQuickViewNavigate"
    />
  </div>
</template>

<script setup lang="ts">
import { useVehiclesStore } from '~/stores/vehicles'
import { useCartStore } from '~/stores/cartStore'
import { computed, ref, onMounted, onBeforeMount, onServerPrefetch } from 'vue'
import { useVehicleQuickView } from '~/layers/base/composables/useVehicleQuickView'
import VehicleQuickView from '~/layers/base/components/VehicleQuickView.vue'
import { ChevronRightIcon, ShoppingCartIcon } from '@heroicons/vue/24/outline'
import FavoriteButton from '~/layers/base/components/FavoriteButton.vue'

const vehiclesStore = useVehiclesStore()
const cartStore = useCartStore()
const { openQuickView } = useVehicleQuickView()

// Quick view state
const selectedVehicleId = ref('')
const isQuickViewOpen = ref(false)

// Get filtered vehicle IDs for navigation
const filteredVehicleIds = computed(() => {
  return latestVehiclesFromSources.value.map(vehicle => vehicle.id)
})

// Handle quick view open
const handleQuickView = (vehicleId: string) => {
  selectedVehicleId.value = vehicleId
  isQuickViewOpen.value = true
  
  // Update URL with quickView parameter
  const url = new URL(window.location.href)
  url.searchParams.set('quickView', vehicleId)
  window.history.pushState({}, '', url.toString())
}

// Handle quick view close
const handleQuickViewClose = () => {
  isQuickViewOpen.value = false
  selectedVehicleId.value = ''
  
  // Remove quickView parameter from URL
  const url = new URL(window.location.href)
  url.searchParams.delete('quickView')
  window.history.replaceState({}, '', url.toString())
}

// Handle quick view navigation
const handleQuickViewNavigate = (vehicleId: string) => {
  selectedVehicleId.value = vehicleId
  
  // Update URL with new quickView parameter
  const url = new URL(window.location.href)
  url.searchParams.set('quickView', vehicleId)
  window.history.replaceState({}, '', url.toString())
}

interface ExtendedVehicle extends Omit<any, 'thumb'> {
  id: string;
  isLatestFromSource?: boolean;
  isNew?: boolean;
  thumb?: string | null;
  price?: number;
  perweek?: number;
  condition?: {
    displayValue?: string[];
  };
  body?: {
    displayValue?: string[];
  };
  transmission?: {
    displayValue?: string[];
  };
  fuel?: {
    displayValue?: string[];
  };
  kms?: number;
}

// Helper to normalize URL
const normalizeUrl = (url: string) => {
  if (!url) return ''
  // Remove any trailing slashes
  url = url.replace(/\/$/, '')
  // Ensure URL starts with http:// or https://
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url
  }
  return url
}

// Get latest vehicles from each source
const latestVehiclesFromSources = computed(() => {
  if (!vehiclesStore.vehicles || vehiclesStore.vehicles.length === 0) {
    return []
  }

  // Get all vehicles and sort by date (assuming newest first)
  const sortedVehicles = [...vehiclesStore.vehicles].sort((a, b) => {
    const vehicleA = a as ExtendedVehicle
    const vehicleB = b as ExtendedVehicle
    
    // First check if vehicle has isNew flag
    if (vehicleA.isNew && !vehicleB.isNew) return -1
    if (!vehicleA.isNew && vehicleB.isNew) return 1

    // Then sort by price (highest first) as secondary criteria
    const priceA = normalizePrice(vehicleA.price)
    const priceB = normalizePrice(vehicleB.price)
    return priceB - priceA
  })

  // Take the first 6 vehicles and ensure they are ExtendedVehicle type
  return sortedVehicles.slice(0, 6).map(vehicle => {
    const extendedVehicle = vehicle as ExtendedVehicle
    return {
      ...extendedVehicle,
      isLatestFromSource: true // Mark all displayed vehicles as latest
    }
  })
})

// Helper to normalize price
const normalizePrice = (price: any): number => {
  if (typeof price === 'number') return price;
  if (typeof price === 'string') return parseFloat(price) || 0;
  if (price && typeof price === 'object' && 'value' in price) {
    const value = price.value;
    if (Array.isArray(value) && value.length > 0) {
      return parseFloat(value[0]) || 0;
    }
    return parseFloat(value) || 0;
  }
  return 0;
}

// Helper to get vehicle title
const getVehicleTitle = (vehicle: any) => {
  if (vehicle.title) {
    return vehicle.title
  }
  if (vehicle.make?.displayValue?.[0] && vehicle.model?.displayValue?.[0]) {
    return `${vehicle.make.displayValue[0]} ${vehicle.model.displayValue[0]}`
  }
  return 'Unknown Vehicle'
}

// Helper to get SEO-friendly vehicle URL
const getVehicleUrl = (vehicle: any) => {
  if (vehicle.slug) {
    return `/vehicle-for-sale/${vehicle.stockid || vehicle.id}/${vehicle.slug}`
  }
  return `/vehicles/${vehicle.stockid || vehicle.id}`
}

// Helper to get vehicle image
const getVehicleImage = (vehicle: ExtendedVehicle) => {
  // First try to get the thumbnail
  if (vehicle.thumb) {
    return normalizeUrl(vehicle.thumb)
  }
  
  // Then try to get the first photo
  if (vehicle.photos && Array.isArray(vehicle.photos) && vehicle.photos.length > 0) {
    return normalizeUrl(vehicle.photos[0])
  }
  
  // Finally try the image property
  if (vehicle.image) {
    return normalizeUrl(vehicle.image)
  }
  
  // If no images available, return placeholder
  return 'https://placehold.co/600x400?text=No+Image'
}

// Function to search for similar vehicles
const searchSimilar = (vehicle: any) => {
  // Extract make and model from title
  const title = getVehicleTitle(vehicle)
  const titleParts = title.split(' ')
  if (titleParts.length >= 2) {
    // Use first two parts of title as search keywords
    const searchTerm = titleParts.slice(0, 2).join(' ')
    // Navigate to vehicles page with search parameter
    const encodedSearchTerm = encodeURIComponent(searchTerm)
    window.location.href = `/vehicles?search_keywords=${encodedSearchTerm}`
  }
}

// Add to Cart function
const addToCart = (vehicleId: string) => {
  if (!vehicleId) {
    console.error("Cannot add to cart: Vehicle ID missing");
    return;
  }
  cartStore.addToCart(vehicleId);
  // cartStore.openCart(); // Optionally open cart
}

// Computed property for current filter query
const currentFilterQuery = computed(() => {
  const query: Record<string, string | string[]> = {}
  
  // Add search query if present
  const searchFilter = vehiclesStore.activeFilters.search_keywords
  if (searchFilter && typeof searchFilter === 'object' && 'values' in searchFilter) {
    query.search = searchFilter.values[0] || ''
  }
  
  // Add other active filters
  Object.entries(vehiclesStore.activeFilters).forEach(([key, value]) => {
    if (value) {
      if (typeof value === 'object') {
        if ('values' in value && Array.isArray(value.values) && value.values.length > 0) {
          query[key] = value.values
        } else if ('type' in value && value.type === 'range') {
          query[key] = `${value.min}-${value.max}`
        }
      }
    }
  })
  
  return query
})
</script>

<style scoped>
.vehicle-card-animation {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.5s ease-out forwards;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.vehicle-card-animation:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style> 